import React, { useState, useEffect } from "react";
import "./styles/base.css";
import "./styles/step1.css";

const DEFAULT_CAPACITY_MESSAGE =
  "We are currently out of on-campus housing for this age group. If you are signing up to participate in activities (VBS, afternoon activities, etc.), please continue filling out the form.\n\nThere are off-campus lodging options available (2\u201310 minute drive). If that is okay, please continue filling out the form and you will be contacted regarding available options.\n\nIf you have any questions or concerns, you can call/text the Registrar, Ashley Johnson, at (217) 491-0013.";

function getCapacityKey(age) {
  if (age === null || age === undefined || Number.isNaN(Number(age))) return "capacity.unknown";
  const a = Number(age);
  if (a >= 0 && a <= 12) return "capacity.kids_0_12";
  if (a >= 13 && a <= 18) return "capacity.youth_13_18";
  if (a >= 19 && a <= 22) return "capacity.college_19_22";
  if (a >= 23 && a <= 30) return "capacity.young_adults_23_30";
  if (a >= 31) return "capacity.adults_31_plus";
  return "capacity.unknown";
}

export default function Step1({ onAgeSelected }) {
  const STORAGE_KEY = "step1Form_v1";

  const loadFromStorage = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const saved = loadFromStorage();

  const [dob, setDob] = useState(saved.dob || "");
  const [grade, setGrade] = useState(saved.grade || "");
  const [age, setAge] = useState(
    typeof saved.age === "number" ? saved.age : saved.age ? Number(saved.age) : null
  );
  const [showGradePrompt, setShowGradePrompt] = useState(false);
  const [pendingDob, setPendingDob] = useState("");
  const [pendingAge, setPendingAge] = useState(null);

  // Capacity settings (fetched once on mount)
  const [capacitySettings, setCapacitySettings] = useState({});

  // Capacity modal: { open, message, onContinue }
  const [capacityModal, setCapacityModal] = useState({ open: false, message: "", onContinue: null });

  // Fetch settings on mount (no auth required)
  useEffect(() => {
    fetch("/.netlify/functions/get-settings")
      .then((r) => r.json())
      .catch(() => ({}))
      .then((json) => {
        setCapacitySettings((json && json.settings) ? json.settings : {});
      })
      .catch(() => {});
  }, []);

  function isCapacityBlocked(calculatedAge) {
    const key = getCapacityKey(calculatedAge);
    return String(capacitySettings[key] || "").trim().toUpperCase() === "TRUE";
  }

  function getCapacityMessage() {
    return String(capacitySettings["capacity.message"] || "").trim() || DEFAULT_CAPACITY_MESSAGE;
  }

  function formatDobMMDDYYYY(input) {
    const digits = String(input || "").replace(/\D/g, "").slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    if (digits.length <= 2) return mm;
    if (digits.length <= 4) return `${mm}/${dd}`;
    return `${mm}/${dd}/${yyyy}`;
  }

  function handleDobChange(e) {
    setDob(formatDobMMDDYYYY(e.target.value));
  }

  function parseDOBtoDate(dobStr) {
    if (!dobStr) return null;
    const s = String(dobStr).trim();

    const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return null;

    const [, mm, dd, yyyy] = m;
    const dt = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (Number.isNaN(dt.getTime())) return null;

    return dt;
  }

  function calculateAge(dobStr) {
    if (!dobStr) return null;
    const today = new Date();
    const dobDate = parseDOBtoDate(dobStr);
    if (!dobDate || Number.isNaN(dobDate.getTime())) return null;

    let a = today.getFullYear() - dobDate.getFullYear();
    const m = today.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) a--;
    return Number.isFinite(a) ? a : null;
  }

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ dob, grade, age }));
    } catch {
      // ignore storage errors
    }
  }, [dob, grade, age]);

  function handleNext() {
    if (!dob) return;
    const calculated = calculateAge(dob);
    setAge(calculated);

    const proceed = () => {
      if (calculated !== null && calculated >= 4 && calculated <= 17) {
        setPendingAge(calculated);
        setPendingDob(dob);
        setShowGradePrompt(true);
        return;
      }
      onAgeSelected(calculated, dob, null);
    };

    if (isCapacityBlocked(calculated)) {
      setCapacityModal({ open: true, message: getCapacityMessage(), onContinue: proceed });
      return;
    }

    proceed();
  }

  function handleGradeSubmit() {
    if (!grade) return;
    setAge(pendingAge);
    onAgeSelected(pendingAge, pendingDob, grade);
    setShowGradePrompt(false);
    setPendingAge(null);
    setPendingDob("");
  }

  function handleCapacityContinue() {
    const fn = capacityModal.onContinue;
    setCapacityModal({ open: false, message: "", onContinue: null });
    if (fn) fn();
  }

  function handleCapacityCancel() {
    setCapacityModal({ open: false, message: "", onContinue: null });
  }

  const gradeOptions = [
    "Pre-K",
    "Kindergarten",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "Freshman",
    "Sophomore",
    "Junior",
    "Senior",
  ];

  return (
    <div className="step1-page">
      <div className="step1-bg" aria-hidden="true">
        <div className="step1-bg__grid">
          {Array.from({ length: 18 }, (_, i) => (i % 9) + 1).map((n, idx) => (
            <div key={idx} className={`step1-bg__tile step1-bg__tile--${n}`} />
          ))}
        </div>
        <div className="step1-bg__overlay" />
      </div>

      {/* Capacity warning modal */}
      {capacityModal.open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="capacity-modal-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            background: "rgba(0,0,0,0.5)"
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 14,
              padding: "28px 24px",
              maxWidth: 520,
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)"
            }}
          >
            <h2
              id="capacity-modal-title"
              style={{ margin: "0 0 14px 0", fontSize: 20, color: "#b91c1c" }}
            >
              Housing Notice
            </h2>
            <p
              style={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.6,
                margin: "0 0 20px 0",
                color: "#374151",
                fontSize: 15
              }}
            >
              {capacityModal.message}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCapacityCancel}
                style={{
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  background: "#f9fafb",
                  color: "#374151",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCapacityContinue}
                style={{
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "none",
                  background: "#2563eb",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="form-container step1-form" role="region" aria-labelledby="step1-title">
        <header>
          <h1 id="step1-title" className="page-title">
            Welcome to Camp Fusion Registration 2026
          </h1>
        </header>

        <div className="welcome-card" role="region" aria-label="Welcome to Camp Fusion">
          <p className="welcome-card__body">
            We are excited you or your camper are joining us! Camp Fusion is a week of worship, fellowship, and
            activities for any age. All prices are per person and cover lodging and meals. If you are registering for a
            group please fill out individual registrations. If you have any questions please contact (217) -491-0013. Financial assistance is available, please fill out the form and you will be contacted about assistance options.
          </p>
          <p className="welcome-card__body" style={{ marginTop: 8 }}>
            Signing a child up for VBS? <a href="/vbs">Click HERE</a>
          </p>

          <details className="pricing-details">
            <summary className="pricing-summary">Pricing (click to expand)</summary>
            <div className="pricing-list">
              <p className="muted">
                <em>All prices are per person.</em>
              </p>
              <ul>
                <li>
                  <strong>Adults (24+):</strong> $260
                </li>
                <li>
                  <strong>Young adults (18–23):</strong> $220
                </li>
                <li>
                  <strong>Youth (13–17):</strong> $175
                </li>
                <li>
                  <strong>Kids (4–12):</strong>
                  <ul className="pricing-children">
                    <li>1st child: $120</li>
                    <li>2nd child: $90</li>
                    <li>3rd+ children: $70 each</li>
                  </ul>
                </li>
                <li>
                  <strong>Children 3 and under:</strong> Free
                </li>
              </ul>
            </div>
          </details>
        </div>

        {!showGradePrompt ? (
          <>
            <section className="field field--dob" aria-label="Camper date of birth">
              <label className="field-label" htmlFor="dob">
                Camper&apos;s Date of Birth <span className="required">*</span>
              </label>

              <input
                id="dob"
                type="text"
                name="dob"
                className="input dob-input"
                value={dob}
                onChange={handleDobChange}
                required
                autoComplete="bday"
                aria-required="true"
                inputMode="numeric"
                placeholder="MM/DD/YYYY"
                maxLength={10}
              />
            </section>

            <div className="form-footer form-footer--center" aria-hidden={false}>
              <button type="button" className="btn btn-primary" onClick={handleNext} disabled={!dob}>
                Next
              </button>
            </div>
          </>
        ) : (
          <div className="grade-prompt" role="dialog" aria-modal="true" aria-labelledby="grade-title">
            <h2 id="grade-title">Please choose the camper&apos;s last completed school level</h2>

            <div className="field">
              <label className="field-label" htmlFor="grade">
                Grade completed <span className="required">*</span>
              </label>

              <select id="grade" name="grade" className="select" value={grade} onChange={(e) => setGrade(e.target.value)}>
                <option value="">Select school level</option>
                {gradeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-footer form-footer--center" aria-hidden={false}>
              <button type="button" className="btn btn-primary" onClick={handleGradeSubmit} disabled={!grade}>
                Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}